import React, { useEffect } from 'react';
import { Redirect, Route, RouteComponentProps, RouteProps } from 'react-router-dom';
import { onlineStatusAtom, usersAtom } from '../utils/Global';
import { useAtomValue, useAtom } from 'jotai';
import Offline from '../pages/Offline';
import { IonSpinner } from '@ionic/react';
import odoo from '../utils/odoo';

type RouteComponentRequiredProps = RouteProps & {
    component: React.ComponentType<RouteComponentProps<any>> | React.ComponentType<any>;
};

export const LoggedRoute: React.FC<RouteComponentRequiredProps> = ({ component: ChildComponent, ...rest }): any => {
    const { mainUser } = useAtomValue(usersAtom);
    const isOnline = useAtomValue(onlineStatusAtom);

    return (
        <Route
            {...rest}
            render={(props) =>
                mainUser ? (
                    isOnline === 'offlineNotInit' ? (
                        <Offline />
                    ) : (
                        <ChildComponent {...props} />
                    )
                ) : (
                    <Redirect
                        to={{
                            pathname: '/login',
                            state: { from: props.location },
                        }}
                    />
                )
            }
        />
    );
};

export const UnLoggedRoute: React.FC<RouteComponentRequiredProps & { redirecTo: string | null }> = ({
    component: ChildComponent,
    redirecTo,
    ...rest
}): any => {
    const { mainUser } = useAtomValue(usersAtom);
    const isOnline = useAtomValue(onlineStatusAtom);
    return (
        <Route
            {...rest}
            render={(props) =>
                mainUser && redirecTo ? (
                    <Redirect
                        to={{
                            pathname: redirecTo,
                        }}
                    />
                ) : isOnline === 'offlineNotInit' ? (
                    <Offline />
                ) : (
                    <ChildComponent {...props} />
                )
            }
        />
    );
};

export const IframeRoute: React.FC<RouteComponentRequiredProps> = ({ component: ChildComponent, ...rest }) => {
    const [{ mainUser }, setUser] = useAtom(usersAtom);

    useEffect(() => {
        if (mainUser) {
            return;
        }

        // Signal to parent that we're ready to receive auth data
        window.parent.postMessage({ type: 'ready' }, '*');

        const handleMessage = async (event: MessageEvent) => {
            if (event.data?.type === 'auth' && event.data?.token) {
                try {
                    const { token, login } = event.data;
                    const formattedUser = odoo._formatToken(token, login, window.location.origin);

                    setUser({ mainUser: formattedUser });
                } catch (e) {
                    console.error('Error authenticating with token:', e);
                }
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [setUser, mainUser]);

    return (
        <Route
            {...rest}
            render={(props) =>
                !mainUser ? (
                    <div className="grid h-screen place-items-center">
                        <div className="text-center">
                            <IonSpinner />
                        </div>
                    </div>
                ) : (
                    <ChildComponent {...props} />
                )
            }
        />
    );
};
